import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { latestPosts } from '../../mocks/posts';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function Home() {
  useEffect(() => {
    document.title = 'Justine Sacco | Chief Communications Officer | Strategic Communications Advisor';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Justine Sacco is a Chief Communications Officer and strategic advisor with twenty years of experience leading global communications for tech and consumer brands including Match Group, Flo Health, FanDuel, and IAC.');

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Person',
          '@id': `${SITE_URL}/#person`,
          name: 'Justine Sacco',
          jobTitle: 'Chief Communications Officer',
          description: 'Strategic communications advisor with twenty years of experience leading global communications for tech and consumer brands including Match Group, Flo Health, FanDuel, and IAC.',
          url: SITE_URL,
          sameAs: [
            'https://www.linkedin.com/in/justinesacco',
          ],
          worksFor: {
            '@type': 'Organization',
            name: 'Flo Health',
            url: 'https://flo.health',
          },
          alumniOf: {
            '@type': 'CollegeOrUniversity',
            name: 'Tulane University',
          },
          knowsAbout: [
            'Strategic Communications',
            'Crisis Communications',
            'Corporate Communications',
            'Reputation Management',
            'Media Relations',
            'Leadership',
          ],
        },
        {
          '@type': 'WebSite',
          '@id': `${SITE_URL}/#website`,
          url: SITE_URL,
          name: 'Justine Sacco',
          description: 'Justine Sacco — Communications executive and advisor. Insights on media, reputation, and leadership.',
          publisher: { '@id': `${SITE_URL}/#person` },
          inLanguage: 'en-US',
        },
        {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/`,
          url: `${SITE_URL}/`,
          name: 'Justine Sacco | Communications Executive & Advisor',
          isPartOf: { '@id': `${SITE_URL}/#website` },
          about: { '@id': `${SITE_URL}/#person` },
          description: 'Justine Sacco — Communications executive and advisor. Insights on media, reputation, and leadership.',
          dateModified: '2026-04-02',
          inLanguage: 'en-US',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: `${SITE_URL}/`,
              },
            ],
          },
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-home';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('schema-home');
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-6">
        <div className="max-w-7xl w-full text-center">
          <h1 
            className="text-[clamp(4rem,15vw,12rem)] leading-[0.9] tracking-tight mb-8"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900 }}
          >
            JUSTINE<br />SACCO
          </h1>
          <p 
            className="text-xl md:text-2xl font-light tracking-wide text-gray-300 max-w-3xl mx-auto"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Communications strategist. Twenty years. No filler.
          </p>
        </div>
        
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2 cursor-pointer hover:border-white/60 transition-colors">
            <div className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Statement Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-black to-zinc-950">
        <div className="max-w-4xl mx-auto">
          <p 
            className="text-2xl md:text-3xl font-light leading-relaxed text-gray-200"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            I build narratives that hold under pressure. Twenty years across agencies, tech platforms, and consumer brands taught me one thing: the story you tell matters less than whether you can defend it when everything breaks. I work with companies that understand the difference.
          </p>
        </div>
      </section>

      {/* Latest Writing */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-baseline justify-between mb-16">
            <h2 
              className="text-5xl md:text-7xl"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900 }}
            >
              LATEST
            </h2>
            <Link 
              to="/writing" 
              className="text-lg font-medium text-[#FF4D00] hover:text-[#FF6B2C] transition-colors whitespace-nowrap"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              View All →
            </Link>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {latestPosts.map((post, index) => (
              <Link 
                key={index}
                to={`/writing#${post.slug}`}
                className="group cursor-pointer"
              >
                <div className="border-t-2 border-[#FF4D00] pt-6 hover:border-[#FF6B2C] transition-colors">
                  <h3 
                    className="text-2xl mb-4 group-hover:text-[#FF4D00] transition-colors"
                    style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900 }}
                  >
                    {post.title}
                  </h3>
                  <p 
                    className="text-gray-400 leading-relaxed mb-4"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {post.excerpt}
                  </p>
                  <span className="text-sm text-gray-500">{post.date}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
